/**
 * Rate Card — Card view (contracts-plan.md §11 tab 3, §7.1 to §7.5; §13.5
 * WP-41). Site selector, therapeutic-area toggle, search; sections in §5.2
 * order with Low, Preferred, High, count, latest sponsor and date; greyed
 * overall values for an area without datapoints (§7.2); datapoint drill-down;
 * pending datapoints panel; the override flow (§7.4); Medicare and Industry
 * mount points (WP-43, WP-44 — placeholders only, per the orchestrator's
 * instruction not to create their sub-folders here, the same slot pattern
 * AdminTab.tsx uses for its own later-WP panels).
 *
 * The SITE SELECTOR is independent of the ambient X-Site-Id (ContractsProvider
 * / useContracts): that header always carries the app's currently-selected
 * site for entitlement purposes (plan §4.9), while this control chooses which
 * site's CARD to view (or "Company-wide" for the network scope) — the sites
 * offered are every site the signed-in user has access to (useAuth's
 * availableSites), per this WP's brief.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Table as TableIcon } from 'lucide-react';
import { FilterSelect, SearchInput, EmptyState, ToastProvider } from '../../../ui';
import { useAuth } from '../../../contexts/AuthContext';
import { useContracts } from '../../ContractsProvider';
import { listMasterEntries } from '../../api/client';
import type { MasterEntry } from '../../api/client';
import { getRateCard } from '../../api/rateCard';
import type { RateCardEntryRow, RateCardView as RateCardViewShape, RcOverride, RcOverrideField, RcScope, OverrideResult, SetPreferredResult } from '../../api/rateCard';
import { PendingPanel } from './PendingPanel';
import { LearningPanel } from './LearningPanel';
import { DatapointsModal } from './DatapointsModal';
import { ValueEditModal } from './ValueEditModal';
import { MedicareSection } from './medicare/MedicareSection';
import { IndustrySection } from './industry/IndustrySection';

/** §5.2's own section order and display labels (kept local to this tab; see rateCard/view.js SECTION_ORDER). */
const SECTION_LABELS: Record<string, string> = {
  per_patient: 'Per patient',
  unitized: 'Unitized',
  staff_rate: 'Staff rate',
  stipend: 'Stipend',
  site_fee_required: 'Basic site fee (required)',
  site_fee_other_required: 'Other fee (required at site)',
  site_fee_other_desired: 'Other fee (desired at site)',
  overhead: 'Institutional overhead',
  holdback: 'Holdback',
  screen_fail: 'Screen fail',
  multi_year: 'Multi-year studies',
};

const NETWORK_OPTION = '__network__';

/** One labelled "not built yet" slot — see AdminTab.tsx's identical pattern. */
function PanelSlot({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-300 p-4 dark:border-gray-600">
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">{title}</h4>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{description}</p>
    </div>
  );
}

function ValueCell({
  value,
  override,
  greyed,
  title,
  onClick,
  testId,
}: {
  value: number | null;
  override?: RcOverride;
  greyed: boolean;
  title: string;
  onClick?: () => void;
  testId: string;
}) {
  return (
    <span
      data-testid={testId}
      title={title}
      onClick={
        onClick
          ? (e) => {
              e.stopPropagation();
              onClick();
            }
          : undefined
      }
      className={`inline-flex items-center gap-1.5 ${
        onClick ? 'cursor-pointer underline decoration-dotted underline-offset-2 hover:text-primary-600 dark:hover:text-primary-400' : ''
      } ${greyed ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}
    >
      {value ?? '—'}
      {override && (
        <span
          data-testid={`${testId}-override`}
          title={`Override: ${override.override_value ?? '—'} — ${override.reason}${
            override.out_of_range ? ' (now outside the computed range)' : ''
          }`}
          className="text-xs font-medium text-amber-700 dark:text-amber-400"
        >
          → {override.override_value}
        </span>
      )}
    </span>
  );
}

function EntryRowView({
  row,
  onOpenDrillDown,
  onOpenEdit,
}: {
  row: RateCardEntryRow;
  onOpenDrillDown: (row: RateCardEntryRow) => void;
  onOpenEdit: (row: RateCardEntryRow, field: RcOverrideField) => void;
}) {
  const lowOverride = row.overrides.find((o) => o.field === 'low');
  const preferredOverride = row.overrides.find((o) => o.field === 'preferred');
  const highOverride = row.overrides.find((o) => o.field === 'high');
  const canEdit = row.value_id !== null;

  return (
    <tr
      data-testid={`rc-row-${row.entry_id}`}
      onClick={canEdit ? () => onOpenDrillDown(row) : undefined}
      className={`transition-colors duration-150 ${canEdit ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50' : ''}`}
    >
      <td className="px-4 py-3">
        <div className="font-medium text-gray-900 dark:text-gray-100">{row.official_name}</div>
        {row.aliases.length > 0 && <div className="text-xs text-gray-500 dark:text-gray-400">{row.aliases.join(', ')}</div>}
        {row.rules.length > 0 && (
          <div className="mt-0.5 text-xs italic text-gray-500 dark:text-gray-400">{row.rules.map((r) => r.text).join('; ')}</div>
        )}
        {row.greyed && (
          <div className="mt-0.5 text-[10px] uppercase tracking-widest text-gray-400 dark:text-gray-500" title={row.greyed_reason ?? undefined}>
            overall value shown
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{row.kind}</td>
      <td className="px-4 py-3">
        <ValueCell
          testId={`rc-value-low-${row.entry_id}`}
          value={row.low}
          override={lowOverride}
          greyed={row.greyed}
          title={`Low — derived: min of ${row.count} accepted datapoint(s) (§7.1, G8)`}
          onClick={canEdit ? () => onOpenEdit(row, 'low') : undefined}
        />
      </td>
      <td className="px-4 py-3">
        <ValueCell
          testId={`rc-value-preferred-${row.entry_id}`}
          value={row.preferred}
          override={preferredOverride}
          greyed={row.greyed}
          title={`Preferred — maintained${row.preferred_source ? `, source: ${row.preferred_source}` : ''} (§7.1, G8)`}
          onClick={canEdit ? () => onOpenEdit(row, 'preferred') : undefined}
        />
      </td>
      <td className="px-4 py-3">
        <ValueCell
          testId={`rc-value-high-${row.entry_id}`}
          value={row.high}
          override={highOverride}
          greyed={row.greyed}
          title={`High — derived: max of ${row.count} accepted datapoint(s) (§7.1, G8)`}
          onClick={canEdit ? () => onOpenEdit(row, 'high') : undefined}
        />
      </td>
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300" title="Count — accepted datapoints behind this value (R15)">
        {row.count}
      </td>
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
        {row.latest ? (
          <span title={`Latest — ${row.latest.sponsor_name ?? 'unknown sponsor'} on ${row.latest.date ?? '—'}; open the row for this datapoint's label and provenance (§7.1, G7, G8)`}>
            {row.latest.value ?? '—'} · {row.latest.sponsor_name ?? '—'} · {row.latest.date ?? '—'}
          </span>
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}

function CardViewInner() {
  const { availableSites } = useAuth();
  const { siteId: ambientSiteId, networkId } = useContracts();

  const [scope, setScope] = useState<RcScope>('site');
  const [siteId, setSiteId] = useState<string | null>(ambientSiteId);
  const [therapeuticAreaId, setTherapeuticAreaId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<RateCardViewShape | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [areas, setAreas] = useState<MasterEntry[]>([]);
  const [drillDown, setDrillDown] = useState<RateCardEntryRow | null>(null);
  const [editing, setEditing] = useState<{ row: RateCardEntryRow; field: RcOverrideField } | null>(null);

  // The ambient site can arrive after this component's first render (sites
  // load asynchronously); adopt it once, without overriding a choice the
  // person already made in this control.
  useEffect(() => {
    if (ambientSiteId && siteId === null && scope === 'site') setSiteId(ambientSiteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambientSiteId]);

  useEffect(() => {
    if (!networkId) return;
    listMasterEntries('therapeutic_area')
      .then(setAreas)
      .catch(() => setAreas([]));
  }, [networkId]);

  const refresh = useCallback(() => {
    if (!networkId) return;
    getRateCard({
      scope,
      siteId: scope === 'site' ? siteId ?? undefined : undefined,
      therapeuticAreaId: therapeuticAreaId ?? undefined,
    })
      .then((result) => {
        setView(result);
        setLoadError(null);
      })
      .catch(() => setLoadError('Could not load the rate card.'));
  }, [networkId, scope, siteId, therapeuticAreaId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const filteredSections = useMemo(() => {
    if (!view) return [];
    const term = search.trim().toLowerCase();
    if (!term) return view.sections;
    return view.sections
      .map((group) => ({
        ...group,
        entries: group.entries.filter((row) => {
          const haystack = [row.official_name, ...row.aliases, row.cpt ?? '', row.section].join(' ').toLowerCase();
          return haystack.includes(term);
        }),
      }))
      .filter((group) => group.entries.length > 0);
  }, [view, search]);

  function applyOverrideResult(target: RateCardEntryRow, field: RcOverrideField, result: OverrideResult) {
    setView((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: prev.sections.map((group) => ({
          ...group,
          entries: group.entries.map((entry) => {
            if (entry.entry_id !== target.entry_id || entry.kind !== target.kind) return entry;
            const kept = entry.overrides.filter((o) => o.field !== field);
            return { ...entry, overrides: [...kept, result.override] };
          }),
        })),
      };
    });
  }

  function applyPreferredResult(target: RateCardEntryRow, result: SetPreferredResult) {
    setView((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        sections: prev.sections.map((group) => ({
          ...group,
          entries: group.entries.map((entry) =>
            entry.entry_id === target.entry_id && entry.kind === target.kind
              ? { ...entry, preferred: result.value.preferred, preferred_source: result.value.preferred_source }
              : entry
          ),
        })),
      };
    });
  }

  if (!networkId) {
    return <EmptyState icon={TableIcon} title="No network context" hint="Choose a site to see its rate card." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          label="Scope"
          auto
          value={scope === 'network' ? NETWORK_OPTION : siteId ?? ''}
          onChange={(v) => {
            if (v === NETWORK_OPTION) {
              setScope('network');
              setSiteId(null);
            } else {
              setScope('site');
              setSiteId(v);
            }
          }}
          options={[{ value: NETWORK_OPTION, label: 'Company-wide' }, ...availableSites.map((s) => ({ value: s.id, label: s.name }))]}
        />
        <FilterSelect
          label="Therapeutic area"
          auto
          value={therapeuticAreaId ?? ''}
          onChange={(v) => setTherapeuticAreaId(v || null)}
          options={[{ value: '', label: 'All areas' }, ...areas.map((a) => ({ value: a.id, label: a.official_name }))]}
        />
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search entry, alias, CPT, section…"
          label="Search rate card"
          className="flex-1 basis-64"
        />
      </div>

      {loadError && <p className="text-sm text-red-600 dark:text-red-400">{loadError}</p>}
      {view === null && !loadError && <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>}

      {view !== null && filteredSections.length === 0 && (
        <EmptyState
          icon={TableIcon}
          title={view.sections.length === 0 ? 'No rate-card entries yet' : 'No entries match'}
          hint={view.sections.length === 0 ? 'Accept datapoints below to start building the card.' : 'Try a different search.'}
          variant="card"
        />
      )}

      {filteredSections.map((group) => (
        <div key={group.section} className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-widest text-gray-500 dark:bg-gray-800/50 dark:text-gray-400">
            {SECTION_LABELS[group.section] ?? group.section}
          </div>
          <table className="w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                {['Name', 'Kind', 'Low', 'Preferred', 'High', 'Count', 'Latest'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {group.entries.map((row) => (
                <EntryRowView
                  key={`${row.entry_id}-${row.kind}`}
                  row={row}
                  onOpenDrillDown={setDrillDown}
                  onOpenEdit={(r, field) => setEditing({ row: r, field })}
                />
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <div>
        <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">Pending datapoints</h3>
        <LearningPanel onChanged={refresh} values={view ? view.sections.flatMap((g) => g.entries) : undefined} />
        <PendingPanel onChanged={refresh} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <MedicareSection scope={scope} siteId={siteId} therapeuticAreaId={therapeuticAreaId} />
        <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
          <h4 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-200">Industry rate card</h4>
          <IndustrySection />
        </div>
      </div>

      {drillDown && (
        <DatapointsModal
          isOpen
          onClose={() => setDrillDown(null)}
          entryId={drillDown.entry_id}
          officialName={drillDown.official_name}
          kind={drillDown.kind}
        />
      )}

      {editing !== null &&
        (() => {
          const { row, field } = editing;
          const valueId = row.value_id;
          if (valueId === null) return null;
          return (
            <ValueEditModal
              isOpen
              onClose={() => setEditing(null)}
              valueId={valueId}
              field={field}
              computedValue={field === 'high' ? row.high : row.low}
              currentPreferred={row.preferred}
              onOverrideSaved={(result) => applyOverrideResult(row, field, result)}
              onPreferredSaved={(result) => applyPreferredResult(row, result)}
            />
          );
        })()}
    </div>
  );
}

export function CardView() {
  return (
    <ToastProvider>
      <CardViewInner />
    </ToastProvider>
  );
}

export default CardView;
